import { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { AppContext } from "./Contexts";
import { getJirItemUrl } from "./api/api";
import { useQuery } from "./AppLayout";
import { navigateTo } from "./Navigator";
import NotificationNavItem from "./notifications/NotificationNavItem";
import {
  FolderKanban,
  Cog,
  PackagePlus,
  Bell,
  CalendarRange,
  ChartArea,
  Target,
  DatabaseZap,
  Boxes,
  PanelLeftClose,
  PanelLeftOpen,
  SquareCheck,
  NotepadText,
  ChevronDown,
  ChevronRight,
  UserPlusIcon,
  Route,
  User,
  CircleUser,
  AtSign,
  CircuitBoard,
  Cone,
  Receipt,
  CodeXml,
  Mail,
  FolderPlus,
  SettingsIcon,
  SquarePen,
  Eye,
  BotMessageSquare,
  Megaphone,
} from "lucide-react";
import DealBadge from "./icons/DealBadge";
import CompanyBadge from "./icons/CompanyBadge";
import ContactBadge from "./icons/ContactBadge";
import MembersIcon from "./icons/MembersIcon";
import PipelinesIcon from "./icons/PipelinesIcon";
import DataFieldsIcon from "./icons/DataFieldsIcon";
import MetaAIIcon from "./icons/MetaAIIcon";
import PipelinePicker from "./PipelinePicker";
import LayoutAction from "./actions/LayoutAction";
import AppNavSettings from "./AppNavSettings";
import AppNavFavoriteTree, { keepFavoriteSavedFilterByIds } from "./AppNavFavoriteTree";
import { useChatAgentLoopFlag } from "./chat/hooks/useChatAgentLoopFlag";
import { useGtmLeadsFlag } from "./gtm/hooks/useGtmLeadsFlag";

const BellNavIcon = (props: any) => <Bell strokeWidth={1.5} {...props} />;
const SquareCheckNavIcon = (props: any) => <SquareCheck strokeWidth={1.5} {...props} />;
const NotepadTextNavIcon = (props: any) => <NotepadText strokeWidth={1.5} {...props} />;
const MailNavIcon = (props: any) => <Mail strokeWidth={1.5} {...props} />;
const BotMessageSquareNavIcon = (props: any) => <BotMessageSquare strokeWidth={1.5} {...props} />;
const MegaphoneNavIcon = (props: any) => <Megaphone strokeWidth={1.5} {...props} />;
const EyeNavIcon = (props: any) => <Eye strokeWidth={1.5} {...props} />;
const SquarePenNavIcon = (props: any) => <SquarePen strokeWidth={1.5} {...props} />;
const RouteNavIcon = (props: any) => <Route strokeWidth={1.5} {...props} />;

const PackagePlusIcon = (props: any) => <PackagePlus strokeWidth={1.5} {...props} />;
const FolderKanbanIcon = (props: any) => <FolderKanban strokeWidth={1.5} {...props} />;
const TeamSettingsIcon = (props: any) => <Cog strokeWidth={1.5} {...props} />;
const BuildingNavIcon = ({ size, _className, ...props }: any) => (
  <CompanyBadge size={size ?? 20} {...props} />
);
const CalendarNavIcon = (props: any) => <CalendarRange strokeWidth={1.5} {...props} />;
const ReportsNavIcon = (props: any) => <ChartArea strokeWidth={1.5} {...props} />;
const DealsNavIcon = ({ size, _className, ...props }: any) => (
  <DealBadge size={size ?? 20} {...props} />
);
const UsersNavIcon = ({ size, _className, ...props }: any) => (
  <ContactBadge size={size ?? 20} {...props} />
);
const MembersNavIcon = (props: any) => <MembersIcon {...props} />;
const PipelinesNavIcon = (props: any) => <PipelinesIcon {...props} />;
const TargetNavIcon = (props: any) => <Target strokeWidth={1.5} {...props} />;
const DatabaseNavIcon = (props: any) => <DatabaseZap strokeWidth={1.5} {...props} />;
const ProductsNavIcon = (props: any) => <Boxes strokeWidth={1.5} {...props} />;
const UserNavIcon = (props: any) => <User strokeWidth={1.5} {...props} />;
const CircleUserNavIcon = (props: any) => <CircleUser strokeWidth={1.5} {...props} />;
const AtSignNavIcon = (props: any) => <AtSign strokeWidth={1.5} {...props} />;

const CircuitBoardNavIcon = (props: any) => <CircuitBoard strokeWidth={1.5} {...props} />;
const ConeNavIcon = (props: any) => <Cone strokeWidth={1.5} {...props} />;
const Grid2X2PlusNavIcon = (props: any) => <DataFieldsIcon {...props} />;

const ReceiptNavIcon = (props: any) => <Receipt strokeWidth={1.5} {...props} />;
const CodeXmlNavIcon = (props: any) => <CodeXml strokeWidth={1.5} {...props} />;

export const GoalIcon = ({ className = "" }) => {
  return (
    <svg
      className={className}
      role="presentation"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <g fillRule="evenodd">
        <path
          d="M16 9C16 12.866 12.866 16 9 16C5.13401 16 2 12.866 2 9C2 5.13401 5.13401 2 9 2C12.866 2 16 5.13401 16 9ZM18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9ZM11 9C11 10.1046 10.1046 11 9 11C7.89543 11 7 10.1046 7 9C7 7.89543 7.89543 7 9 7C10.1046 7 11 7.89543 11 9ZM13 9C13 11.2091 11.2091 13 9 13C6.79086 13 5 11.2091 5 9C5 6.79086 6.79086 5 9 5C11.2091 5 13 6.79086 13 9Z"
          fillRule="evenodd"
        ></path>
      </g>
    </svg>
  );
};

export const ReportIcon = ({ className = "" }) => {
  return (
    <svg
      className={className}
      role="presentation"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M2.75 3c-.41 0-.75.34-.75.75v3.8c-.01 3.87-.02 8.47 0 11.47 0 1.1.9 1.98 2 1.98h17.25c.41 0 .75-.34.75-.75v-.23c0-.07-.03-.15-.1-.2L15 14.3c-.1-.08-.24-.07-.33.03l-3.4 3.65c-.09.1-.24.1-.34.02l-2.67-2.17c-.1-.09-.26-.07-.35.03l-2.97 3.55c-.05.06-.12.09-.2.09H4c-.28 0-.5-.22-.5-.5v-.52L8 13.06 10.59 15c.3.25.76.22 1.03-.07l4.63-5.02V12c0 .41.34.75.75.75s.75-.34.75-.75V8c0-.41-.34-.75-.75-.75h-4c-.41 0-.75.34-.75.75s.34.75.75.75h2.29L11 13.4l-2.52-1.98c-.32-.26-.8-.22-1.06.1l-3.92 4.7V3.76c0-.41-.34-.75-.75-.75Z" />
    </svg>
  );
};

export const listNav = [
  {
    name: "Notifications",
    icon: BellNavIcon,
  },
  {
    name: "Ask",
    icon: BotMessageSquareNavIcon,
  },
  {
    name: "Leads",
    icon: MegaphoneNavIcon,
  },
  {
    name: "Content studio",
    icon: MetaAIIcon,
  },
  {
    name: "Tasks",
    icon: SquareCheckNavIcon,
  },
  {
    name: "Notes",
    icon: NotepadTextNavIcon,
  },
  {
    name: "Emails",
    icon: MailNavIcon,
  },
  {
    name: "Drafts",
    icon: SquarePenNavIcon,
  },
  {
    name: "Outbox",
    icon: MailNavIcon,
  },
  {
    name: "Tracked",
    icon: EyeNavIcon,
  },
  {
    name: "Deals",
    icon: DealsNavIcon,
  },
  {
    name: "Reports",
    icon: ReportsNavIcon,
  },
  {
    name: "Accounts",
    icon: BuildingNavIcon,
  },
  {
    name: "Contacts",
    icon: UsersNavIcon,
  },
  {
    name: "Products",
    icon: ProductsNavIcon,
  },
  {
    name: "Activities",
    icon: CalendarNavIcon,
  },
  {
    name: "Automations",
    icon: RouteNavIcon,
  },
  {
    name: "Goals",
    icon: TargetNavIcon,
  },
  {
    name: "DataFields",
    icon: DatabaseNavIcon,
  },
  {
    name: "Apps",
    icon: PackagePlusIcon,
  },
  {
    name: "Pipeline Settings",
    icon: FolderKanbanIcon,
  },
  {
    name: "Team Settings",
    icon: TeamSettingsIcon,
  },
  {
    name: "User Settings",
    icon: UserNavIcon,
  },
];

export const workspaceNav = (workspaceId) => [
  {
    name: "Ask",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      key: "Ask",
      suffix: "ask",
    }),
    suffix: "ask",
    search: "?key=Ask",
    icon: BotMessageSquareNavIcon,
  },
  {
    name: "GTM",
    children: [
      {
        name: "Leads",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          key: "Leads",
          suffix: "gtm/leads",
        }),
        suffix: "gtm/leads",
        search: "?key=Leads",
        icon: MegaphoneNavIcon,
      },
      {
        name: "Content studio",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          key: "Content studio",
          suffix: "ai-content",
        }),
        suffix: "ai-content",
        search: "?key=Content%20studio",
        icon: MetaAIIcon,
      },
    ],
  },
  {
    name: "Notifications",
    href: `/workspaces/${workspaceId}/notifications`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      key: "Notifications",
      suffix: "notifications",
    }),
    suffix: "notifications",
    search: "?key=Notifications",
    icon: BellNavIcon,
  },
  {
    name: "Tasks",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "GRID",
      type: "ACTIVITY",
      key: "Tasks",
    }),
    suffix: "",
    search: "?view=GRID&type=ACTIVITY&key=Tasks",
    icon: SquareCheckNavIcon,
  },
  {
    name: "Notes",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "GRID",
      type: "ACTIVITY",
      key: "Notes",
    }),
    suffix: "",
    search: "?view=GRID&type=ACTIVITY&key=Notes",
    icon: NotepadTextNavIcon,
  },
  {
    name: "Emails",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "GRID",
      type: "ACTIVITY",
      key: "Drafts", // Default drafts
    }),
    suffix: "",
    search: "?view=GRID&type=ACTIVITY&key=Drafts", // Default drafts
    icon: MailNavIcon,
  },
  {
    name: "Reports",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "CHART",
      key: "Reports",
      suffix: "reports",
    }),
    suffix: "reports",
    search: "?key=Reports",
    icon: ReportsNavIcon,
  },
  {
    name: "Automations",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "GRID",
      type: "SALE",
      suffix: "automations",
      key: "Automations",
    }),
    suffix: "automations",
    search: "?key=Automations",
    icon: RouteNavIcon,
  },
  {
    name: "Favorites",
    children: [],
  },
  {
    name: "Records",
    children: [
      {
        name: "Deals",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({ workspaceId, key: "Deals" }),
        suffix: "",
        search: "?view=BOARD&type=SALE&key=Deals",
        icon: DealsNavIcon,
      },
      {
        name: "Accounts",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "GRID",
          type: "COMPANY",
          key: "Accounts",
        }),
        suffix: "",
        search: "?view=GRID&type=COMPANY&key=Accounts",
        icon: BuildingNavIcon,
      },
      {
        name: "Contacts",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "GRID",
          type: "CONTACT",
          key: "Contacts",
        }),
        suffix: "",
        search: "?view=GRID&type=CONTACT&key=Contacts",
        icon: UsersNavIcon,
      },
      {
        name: "Activities",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "CALENDAR",
          type: "ACTIVITY",
          key: "Activities",
        }),
        suffix: "",
        search: "?view=CALENDAR&type=ACTIVITY&key=Activities",
        icon: CalendarNavIcon,
      },
      {
        name: "Products",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "GRID",
          type: "PRODUCT",
          key: "Products",
        }),
        suffix: "",
        search: "?view=GRID&type=PRODUCT&key=Products",
        icon: ProductsNavIcon,
      },
    ],
  },
];

export const workspaceNavSetting = (workspaceId, isJira) => [
  {
    name: "User",
    children: [
      {
        name: "Profile",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/user",
          key: "Profile",
        }),
        suffix: "settings/user",
        search: "?item=profile&key=User%20Settings",
        icon: CircleUserNavIcon,
      },
      {
        name: "Notifications",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/user",
          key: "Notifications",
        }),
        suffix: "settings/user",
        search: "?item=notifications&key=User%20Settings",
        icon: BellNavIcon,
      },
      {
        name: "Email & Calendar",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/user",
          key: "Email & Calendar",
        }),
        suffix: "settings/user",
        search: "?item=mail&key=User%20Settings",
        icon: AtSignNavIcon,
      },
    ],
  },
  {
    name: "Pipeline",
    children: [
      {
        name: "General",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/pipeline",
          key: "General",
        }),
        suffix: "settings/pipeline",
        search: "?item=general&key=Pipeline%20Settings",
        icon: CircuitBoardNavIcon,
      },
      {
        name: "Members",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/pipeline",
          key: "Members",
        }),
        suffix: "settings/pipeline",
        search: "?item=member&key=Pipeline%20Settings",
        icon: MembersNavIcon,
      },
      {
        name: "Sale Funnels",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/pipeline",
          key: "Sale Funnels",
        }),
        suffix: "settings/pipeline",
        search: "?item=salefunnel&key=Pipeline%20Settings",
        icon: ConeNavIcon,
      },
      {
        name: "Data",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/pipeline",
          key: "Data",
        }),
        suffix: "settings/pipeline",
        search: "?item=data&key=Pipeline%20Settings",
        icon: Grid2X2PlusNavIcon,
      },
    ],
  },
  {
    name: "Team",
    children: [
      {
        name: "General",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/team",
          key: "General",
        }),
        suffix: "settings/team",
        search: "?item=general&key=Team%20Settings",
        icon: CircuitBoardNavIcon,
      },
      {
        name: isJira ? "Subscriptions" : "Plan & Billing",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/team",
          key: isJira ? "Subscriptions" : "Plan & Billing",
        }),
        suffix: "settings/team",
        search: `?item=${isJira ? "subscription" : "billing"}&key=Team%20Settings`,
        icon: ReceiptNavIcon,
      },
      {
        name: "Members",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/team",
          key: "Members",
        }),
        suffix: "settings/team",
        search: "?item=member&key=Team%20Settings",
        icon: MembersNavIcon,
      },
      {
        name: "Pipelines",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/team",
          key: "Pipelines",
        }),
        suffix: "settings/team",
        search: "?item=pipeline&key=Team%20Settings",
        icon: PipelinesNavIcon,
      },
      {
        name: "Developer",
        href: `/workspaces/${workspaceId}`,
        jiraUrl: getJirItemUrl({
          workspaceId,
          view: "",
          type: "",
          suffix: "settings/team",
          key: "Developer",
        }),
        suffix: "settings/team",
        search: "?item=developer&key=Team%20Settings",
        icon: CodeXmlNavIcon,
      },
    ],
  },
  {
    name: "break1",
    separator: true,
  },
  {
    name: "Goals",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "GRID",
      type: "SALE",
      suffix: "settings/goals",
      key: "Goals",
    }),
    suffix: "settings/goals",
    search: "?key=Goals",
    icon: TargetNavIcon,
  },
  {
    name: "DataFields",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "",
      type: "SALE",
      suffix: "settings/data-fields",
      key: "DataFields",
    }),
    suffix: "settings/data-fields",
    search: "?key=DataFields&type=SALE",
    icon: DatabaseNavIcon,
  },
  {
    name: "Apps",
    href: `/workspaces/${workspaceId}`,
    jiraUrl: getJirItemUrl({
      workspaceId,
      view: "",
      type: "",
      suffix: "settings/apps",
      key: "Apps",
    }),
    suffix: "settings/apps",
    search: "?key=Apps",
    icon: PackagePlusIcon,
  },
];

export const getCurrentItemAppName = (query, pathname?: string) => {
  const key = query.get("key");
  if (key) {
    return key;
  }

  const type = query.get("type");
  if (type === "SALE") {
    return "Deals";
  } else if (type === "COMPANY") {
    return "Accounts";
  } else if (type === "CONTACT") {
    return "Contacts";
  } else if (type === "PRODUCT") {
    return "Products";
  } else if (type === "ACTIVITY") {
    return "Activities";
  }

  if (pathname) {
    if (pathname.includes("/automations")) return "Automations";
    if (pathname.includes("/reports")) return "Reports";
    if (pathname.includes("/notifications")) return "Notifications";
    if (pathname.includes("/gtm/leads")) return "Leads";
    if (pathname.includes("/ai-content")) return "Content studio";
    if (pathname.includes("/ask")) return "Ask";
  }

  return "";
};

export function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

const AppNavItem = ({ item, isGroup, favoriteTree, currentUser }) => {
  const navigate = useNavigate();
  const { projectId }: any = useParams();
  const query: any = useQuery();
  const location = useLocation();

  const [showGroup, setShowGroup] = useState(true);
  const [isAddFolder, setIsAddFolder] = useState(false);

  if (item.separator) {
    return (
      <li key={item.name} title={item.name}>
        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-sm text-gray-500"></span>
          </div>
        </div>
      </li>
    );
  }

  const currentItemAppName = getCurrentItemAppName(query, location.pathname);
  const isCurrent = item.name == currentItemAppName;

  const menuItem = (
    <>
      {item.icon ? (
        <div
          className={classNames(
            isCurrent ? "text-blue-600" : "text-gray-700 group-hover:text-blue-600",
            "flex h-5 w-5 shrink-0 items-center justify-center"
          )}
          aria-hidden="true"
        >
          <item.icon size={16} />
        </div>
      ) : null}
      <span className="font-medium">{item.name}</span>
    </>
  );

  if (item.name === "Notifications") {
    return (
      <li key={item.name} title={item.name}>
        <NotificationNavItem menuItem={menuItem} isCurrent={isCurrent} currentUser={currentUser} />
      </li>
    );
  }

  if (item.name === "Favorites" && favoriteTree.size === 0) {
    return null;
  }

  if (item.children) {
    const renderMenuButton = () => {
      if (item.name === "Favorites") {
        return (
          <div
            onClick={() => {
              setIsAddFolder(true);
            }}
            title="Create new favorites folder"
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          >
            <FolderPlus className="h-4 w-4 text-gray-600 hover:text-blue-600" />
          </div>
        );
      } else if (item.name === "Records") {
        return (
          <div
            onClick={() => {
              const appUrl = `/workspaces/${projectId}/settings/data-fields?type=SALE&key=DataFields`;
              navigateTo({
                jiraUrlParams: {
                  relativeUrl: getJirItemUrl({
                    workspaceId: projectId,
                    view: "",
                    type: "SALE",
                    suffix: "settings/data-fields",
                    key: "DataFields",
                  }),
                },
                appUrl,
                navigate,
                forceUseNavigate: true,
              });
            }}
            title="Data Fields"
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          >
            <SettingsIcon className="h-4 w-4 text-gray-600 hover:text-blue-600" />
          </div>
        );
      }
      return null;
    };

    const renderListItem = () => {
      if (item.name === "Favorites") {
        return (
          <AppNavFavoriteTree
            favoriteTree={favoriteTree}
            isAddFolder={isAddFolder}
            setIsAddFolder={setIsAddFolder}
          />
        );
      }
      return (
        <ul role="list" className="space-y-1 py-1.5">
          {item.children.map((itemChild) => (
            <AppNavItem
              key={itemChild.name}
              item={itemChild}
              isGroup={true}
              favoriteTree={favoriteTree}
              currentUser={currentUser}
            />
          ))}
        </ul>
      );
    };

    return (
      <li key={item.name} title={item.name} className="mt-3">
        <div className="group flex items-center justify-between gap-1 p-1">
          <div
            onClick={() => {
              setShowGroup(!showGroup);
            }}
            title={item.name}
            className="w-full flex items-center gap-x-2 cursor-pointer"
          >
            {showGroup ? (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-600" />
            )}
            <span className="text-xs font-medium text-gray-600">{item.name}</span>
          </div>
          <div>{renderMenuButton()}</div>
        </div>
        <div
          className={`
              overflow-hidden transition-all duration-300 ease-in-out
              ${showGroup ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}
            `}
        >
          {renderListItem()}
        </div>
      </li>
    );
  }

  const navItemUrl = item.href == "" ? "/workspaces" : `${item.href}/${item.suffix}${item.search}`;

  return (
    <li key={item.name} title={item.name} className={isGroup ? "pl-4" : ""}>
      <Link
        to={navItemUrl}
        onClick={() => {
          navigateTo({
            jiraUrlParams: {
              relativeUrl: item.jiraUrl,
            },
            appUrl: navItemUrl,
            navigate,
          });
        }}
        title={item.name}
        className={classNames(
          isCurrent
            ? "bg-gray-100 text-blue-600"
            : "text-gray-700 hover:text-blue-600 hover:bg-gray-100",
          "group flex items-center gap-x-2 rounded-md p-1 cursor-pointer"
        )}
      >
        {menuItem}
      </Link>
    </li>
  );
};

const AppNav = ({ show }) => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const chatAgentLoopEnabled = useChatAgentLoopFlag();
  const gtmLeadsEnabled = useGtmLeadsFlag();
  const navItems = useMemo(() => {
    let items = projectId ? workspaceNav(projectId) : [];
    if (!chatAgentLoopEnabled) {
      items = items.filter((item) => item.name !== "Ask");
    }
    // Flag off: keep GTM → Content studio; hide Leads (do not strand Content studio).
    if (!gtmLeadsEnabled) {
      items = items.map((item) => {
        if (item.name !== "GTM" || !item.children) return item;
        return {
          ...item,
          children: item.children.filter((child) => child.name !== "Leads"),
        };
      });
    }
    return items;
  }, [projectId, chatAgentLoopEnabled, gtmLeadsEnabled]);
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const showAppNav = useSelector((state: any) => state.layoutReducer.get("appNav"));
  const myFilters = useSelector((state: any) => state.filterReducer.get("myFilters")) || fromJS([]);
  const favoriteTreeAll =
    useSelector((state: any) => state.projectReducer.getIn(["project", "favoriteTree"])) ||
    fromJS([]);

  const favoriteTree = useMemo(() => {
    const filterIds = myFilters.map((item) => item.get("id")).toJS();
    return fromJS(keepFavoriteSavedFilterByIds(favoriteTreeAll.toJS(), filterIds));
  }, [favoriteTreeAll, myFilters]);

  const appContext = useContext<any>(AppContext);

  const hasProject = projectId && projectId != "";

  if (appContext.appName != "main" && appContext.appName != "chrome") {
    return null;
  }

  if (location.pathname && location.pathname.includes("/settings/")) {
    return <AppNavSettings />;
  }

  const renderWorkspaceNavItems = () => {
    if (hasProject) {
      return (
        <li className="w-full">
          <ul role="list" className="space-y-1 p-1.5">
            {navItems.map((item) => (
              <AppNavItem
                key={item.name}
                item={item}
                isGroup={false}
                favoriteTree={favoriteTree}
                currentUser={currentUser}
              />
            ))}
          </ul>
        </li>
      );
    }
  };

  return (
    <div
      className={`${show || showAppNav ? "w-[240px]" : "w-0"} flex-shrink-0 border-r border-gray-200 transition-all duration-300 ease-in-out overflow-hidden`}
    >
      <div className="w-full h-12 px-2 flex items-center justify-between gap-1 border-b border-gray-200 hover:bg-gray-100 cursor-pointer">
        <PipelinePicker />
        {showAppNav ? (
          <div className="flex-shrink-0">
            <PanelLeftClose
              className="w-5 h-5 text-gray-500"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(LayoutAction.showAppNav(false));
              }}
            />
          </div>
        ) : (
          <div className="flex-shrink-0">
            <PanelLeftOpen
              className="w-5 h-5 text-gray-500"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(LayoutAction.showAppNav(true));
              }}
            />
          </div>
        )}
      </div>
      <nav className="w-full h-[calc(100dvh-48px)] flex flex-col">
        <ul
          role="list"
          className="w-full h-full flex items-center flex-1 flex-col gap-y-1.5 overflow-y-auto"
        >
          {renderWorkspaceNavItems()}
        </ul>
        <div className="p-2 border-t border-gray-200">
          <div
            onClick={() => {
              const appUrl = `/workspaces/${projectId}/settings/team?item=member&key=Team%20Settings`;
              navigateTo({
                jiraUrlParams: {
                  relativeUrl: getJirItemUrl({
                    workspaceId: projectId,
                    view: "",
                    type: "",
                    suffix: "settings/team",
                    key: "Members",
                  }),
                },
                appUrl,
                navigate,
                forceUseNavigate: true,
              });
            }}
            className={
              "p-1.5 md:p-2 h-6 md:h-8 space-x-1.5 md:space-x-2 cursor-pointer flex items-center rounded-md hover:bg-gray-100 w-full"
            }
          >
            <UserPlusIcon className={"w-3 md:w-4"} />
            <div className="text-xs md:text-sm">{t("Invite members")}</div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default AppNav;
